import { RESTART_QUERY_VALUE, WizardNavigationContext } from '@lemonade-hq/chat-wizard-client-base';
import { clamp } from '@lemonade-hq/ts-helpers';
import { RESTART_QUERY_PARAM } from '@lemonade-hq/uniclient-chat';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';

interface HistoryState {
  readonly fromWizardNext?: boolean;
  readonly currentStepIndex?: number;
}

interface RouterPopStateEventState {
  /**
   * this is a prop defined by react-router-dom and we use to to detect the popstate navigation destination
   */
  readonly usr?: HistoryState;
}

interface RouterPopStateEvent extends PopStateEvent {
  readonly state: RouterPopStateEventState | undefined;
}

const INITIAL_STEP_INDEX = Number.NaN;

export const getExistingQueryParams = (): string => {
  const params = new URLSearchParams(window.location.search);
  params.delete(RESTART_QUERY_PARAM);
  const result = params.toString();
  return result.length === 0 ? '' : `?${result}`;
};

const getNavigationUrl = (): string => {
  return `${window.location.pathname}${getExistingQueryParams()}`;
};

export const UniclientBrowserNavigationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const currentAnchor = useRef<number | null>(null);

  const setCurrentStepIndex = useCallback(
    (navigate: NavigateFunction, location: Location) => (stepIndex: number) => {
      const locationState = location.state as HistoryState | undefined;
      const isRemovingForceRestart = new URLSearchParams(window.location.search).has(RESTART_QUERY_PARAM);
      const isNewStepNaN = Number.isNaN(stepIndex);
      if (isNewStepNaN) {
        currentAnchor.current = null;
      }

      navigate(getNavigationUrl(), {
        state: { currentStepIndex: stepIndex },
        replace: Number.isNaN(locationState?.currentStepIndex) || isNewStepNaN || isRemovingForceRestart,
      });
    },
    [],
  );

  const handleRestart = useCallback(() => {
    const query = new URLSearchParams(window.location.search);
    query.set(RESTART_QUERY_PARAM, RESTART_QUERY_VALUE);
    query.delete('id');

    const url = new URL(window.location.href);
    url.search = query.toString();
    window.location.assign(url.toString());
  }, []);

  const handleAnchor = useCallback(
    (navigate: NavigateFunction, anchorStepIndex: number) => () => {
      if (currentAnchor.current == null) {
        navigate(getNavigationUrl(), { state: { currentStepIndex: anchorStepIndex }, replace: true });
      }

      currentAnchor.current = anchorStepIndex;
    },
    [],
  );

  const handleBack = useCallback(
    (navigate: NavigateFunction, location: Location) =>
      (levels = 1) => {
        const locationState = location.state as HistoryState | undefined;
        /* istanbul ignore next -- @preserve - not sure how to mock nullish locationState */
        const stepIndex = locationState?.currentStepIndex ?? currentAnchor.current ?? INITIAL_STEP_INDEX;
        const newStepIndex = clamp(currentAnchor.current ?? 0, stepIndex, stepIndex - levels);
        if (locationState?.fromWizardNext) {
          navigate(getNavigationUrl(), { state: { currentStepIndex: newStepIndex } });
        } else {
          navigate(getNavigationUrl(), {
            state: { fromWizardNext: false, currentStepIndex: Math.max(1, newStepIndex) },
          });
        }

        return newStepIndex;
      },
    [],
  );

  const handleNext = useCallback(
    (navigate: NavigateFunction, location: Location) => () => {
      const locationState = location.state as HistoryState | undefined;
      /* istanbul ignore next -- @preserve - not sure how to mock nullish locationState */
      const newStepIndex = (locationState?.currentStepIndex ?? currentAnchor.current ?? 0) + 1;
      navigate(getNavigationUrl(), {
        state: {
          fromWizardNext: true,
          currentStepIndex: newStepIndex,
        },
      });
    },
    [],
  );

  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as HistoryState | undefined;

  /* istanbul ignore next -- @preserve - not sure how to mock nullish locationState */
  const index = locationState?.currentStepIndex ?? currentAnchor.current ?? INITIAL_STEP_INDEX;
  const currentStepIndex = Math.max(currentAnchor.current ?? 0, index);

  useEffect(
    function preventForwardNavigation() {
      function handlePopState(ev: RouterPopStateEvent): void {
        const anchor = currentAnchor.current ?? 0;
        const eventStepIndex = ev.state?.usr?.currentStepIndex ?? 0;
        /* istanbul ignore next -- @preserve - not sure how to mock nullish locationState */
        const currentIndex = locationState?.currentStepIndex ?? 0;

        if (eventStepIndex > currentIndex) {
          navigate(getNavigationUrl(), {
            state: { currentStepIndex: Math.max(anchor, currentIndex) },
            replace: true,
          });
        } else {
          navigate(getNavigationUrl(), {
            state: { currentStepIndex: Math.max(anchor, currentIndex - 1) },
            replace: true,
          });
        }
      }

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    },
    [locationState, navigate],
  );

  const value = useMemo(
    () => ({
      currentStepIndex,
      setCurrentStepIndex: setCurrentStepIndex(navigate, location),
      restart: handleRestart,
      anchor: handleAnchor(navigate, currentStepIndex),
      back: handleBack(navigate, location),
      next: handleNext(navigate, location),
    }),
    [currentStepIndex, handleRestart, handleAnchor, setCurrentStepIndex, handleBack, handleNext, location, navigate],
  );
  return <WizardNavigationContext.Provider value={value}>{children}</WizardNavigationContext.Provider>;
};
